// BlueberryDeliveries — the blueberry flow's intake page (rendered by
// DeliveriesStage when production_flow is BLUEBERRY; citrus packhouses keep the
// existing Deliveries page untouched).
//
// Berry intake is crate-based: berries arrive in blueberry/strawberry crates,
// get batched onto a dolly and force-cooled (Precool), then released to
// production. So this page deliberately has NONE of the citrus intake
// machinery — no drench/colour-up pipeline, no KF49 returnable-bin/empties
// loop, no Phytclean gate. It records the source delivery (grower + variety +
// crates) that Precool's cooling batches trace back to.
//
// "Crates remaining" is deliveries.bins_remaining: creating a cooling batch
// consumes crates from the delivery (mig 823 trigger, mirroring how citrus
// tipping decrements bins) and a CANCELLED batch returns them. Fully-drafted
// deliveries flip to TIPPED and drop off this board + the Precool picker.

import { useEffect, useMemo, useState } from 'react'
import { useHistoryWindow } from '@/hooks/useHistoryWindow'
import { useCanOpenModule } from '@/hooks/useCanOpenModule'
import { POLL } from '@/lib/pollTiers'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Truck, Plus, Snowflake, ChevronRight, Printer, Scale, Trash2, Layers, FileText } from 'lucide-react'
import FieldScopedSearch from '@/components/FieldScopedSearch'
import PageHeader from '@/components/PageHeader'
import QueryStateGate from '@/components/QueryStateGate'
import { supabase } from '@/lib/supabase'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { ValidationError } from '@/lib/errorReporter'
import { useSaveDb, ensureSaveDb } from '@/lib/useSaveDb'
import { withTimeout } from '@/lib/saveTimeout'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useTenantPersistedState } from '@/hooks/useTenantPersistedState'
import { filterDeliveries, FILTER_STORAGE_KEYS, UNSPECIFIED_DELIVERY_POINT, type SearchField } from '@/lib/deliveriesFilter'
import { useDeliveryPoints } from '@/lib/usePartyOptions'
import DeliveryPointPicker from '@/components/DeliveryPointPicker'
import type { Delivery } from '@/types/database'
import { useGrowers, useGrowerBlocks } from '@/hooks/useGrowerData'
import { useVehicleRegs } from '@/hooks/useVehicleRegs'
import { useCommodityVarieties, useCommodityIntakeSizes, useCommodities } from '@/hooks/useCommodities'
import { CRATE_TYPES, CONTAINER_LABEL, crateTypeForCommodity, type ContainerType } from '@/lib/containerTypes'
import { blocksForVariety } from '@/lib/berryIntakeBlocks'
import { useFlowManifest } from '@/hooks/useCommodityTemplates'
import { useTenantFlows, flowLabel, intakeFlowKey, resolveEntityFlowKey } from '@/lib/entityFlow'
import ComboField from '@/components/ComboField'
import { SizeChips, SizeSplitRow } from './DeliverySizeSplit'
import SavedBanner, { SavedBannerLink } from '@/components/SavedBanner'
import { newUuid } from '@/lib/uuid'
import { insertOrQueueCoolingBatch } from '@/lib/offlineQueue'
import { useCoolingDefaults } from '@/lib/useCoolingDefaults'
import { stdCrateGrossKg } from '@/lib/coolingDefaults'
import { useCrateWeightSamples } from '@/hooks/useCrateWeightSamples'
import { sampleAvgFor, estimateDeliveryNetKg, configuredTare } from '@/lib/crateWeightSamples'
import { sastYMD, sastTodayYMD } from '@/lib/dates'
import { useBerryCrateTares } from '@/hooks/useBerryCrateTares'
import { weighTotals, weighGroupNetKg, avgModeToGroup, sizeSplitTotals, splitDollyGross, sizeLinesToPayload, buildSizeLines, PLACEHOLDER_CRATE_TARE_KG, PLAUSIBLE_MAX_FRUIT_PER_CRATE_KG, type WeighGroupInput, type SizeLineInput, type SizeBreakdownRow } from '@/lib/berryWeighing'
import ConfigLink from '@/components/ConfigLink'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { alertDialog } from '@/components/Dialogs'
import { throwFriendly } from '@/lib/friendlyError'
import { FARM_LOAD_DOMAIN } from '@/modules/shared/PendingFarmLoads'
import type { PendingFarmLoad } from '@/hooks/usePendingFarmLoads'
import { farmLoadPrefill, pickLoadBlock } from '@/lib/berryFarmLoad'
import BerryFarmLoadsInbox from './BerryFarmLoadsInbox'
import { Field } from '@/components/Field'
import { readState } from '@/lib/readState'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { coolTheCratesHref, dollyLabelHref, primaryReceiveAction, type BerryReceiveSaved } from './berryReceiveHandoff'

interface DeliveryRow {
  delivery_id:          string
  grower:               string
  variety:              string
  puc:                  string | null
  block:                string | null
  delivery_point:       string | null
  bins_received:        number
  bins_remaining:       number
  status:               string
  delivery_type:        string
  fruit_container_type: ContainerType | null
  vehicle_reg:          string | null
  driver_name:          string | null
  received_at:          string
  notes:                string | null
}

// Berry status chips — same mechanism as the citrus board (shared persisted
// keys via deliveriesFilter.ts, so the report inherits the operator's filters)
// with the berry status vocabulary.
const BERRY_STATUS_FILTERS = ['ALL', 'RECEIVED', 'IN_STOCK', 'TIPPED'] as const
const BERRY_STATUS_LABELS: Record<string, string> = {
  ALL: 'All', RECEIVED: 'Received', IN_STOCK: 'In stock', TIPPED: 'Fully batched',
}
const BERRY_SEARCH_FIELDS: { value: SearchField; label: string }[] = [
  { value: 'any',         label: 'Any field' },
  { value: 'grower',      label: 'Grower' },
  { value: 'variety',     label: 'Variety' },
  { value: 'puc',         label: 'PUC' },
  { value: 'vehicle_reg', label: 'Vehicle' },
  { value: 'driver_name', label: 'Driver' },
]

// delivery_weighing_summary's raw row (mig 1045 + 20260813100000). NUMERICs
// come back over PostgREST as strings, hence the union.
interface SummaryRow {
  delivery_id:    string
  net_kg:         number | string
  crates_weighed: number | string
  reject_crates:  number | string | null
  size_breakdown: SizeBreakdownRow[] | null
}

// What the board card actually needs off that row.
interface DeliverySummary {
  net:     number
  crates:  number
  rejects: number
  sizes:   SizeBreakdownRow[]
}

// Key for the reject row in the size-split form state. A class code can never
// be blank (commodity_intake_sizes CHECK btrim(code) <> ''), so '' is a
// collision-proof sentinel that needs no escaping.
const REJECT_LINE_KEY = ''

const fmt = (s: string) => new Date(s).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })

// Pre-existing large register page (board + filters + intake card).
// fallow-ignore-next-line complexity
export default function BlueberryDeliveries() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  // Explicit saved-confirmation banner after a receive (operator request).
  // UF-12: it keeps the new delivery's id so "Cool the crates →" opens
  // Precool on THIS delivery instead of a bare board.
  const [justSaved, setJustSaved] = useState<BerryReceiveSaved | null>(null)
  // The farm load being received, if the form was opened from the inbox.
  const [farmLoad, setFarmLoad] = useState<PendingFarmLoad | null>(null)
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null
  // /batch-label is route-gated by the precool module: no gate, no link.
  const canOpenPrecool = useCanOpenModule('precool')

  // Filters — the citrus board's mechanism verbatim (shared persisted keys +
  // filterDeliveries), so the printable report inherits whatever is set here.
  const [statusFilter,  setStatusFilter]  = usePersistedState<string>(FILTER_STORAGE_KEYS.statusFilter, 'ALL')
  const [growerFilter,  setGrowerFilter]  = usePersistedState<string>(FILTER_STORAGE_KEYS.growerFilter, '')
  const [varietyFilter, setVarietyFilter] = usePersistedState<string>(FILTER_STORAGE_KEYS.varietyFilter, '')
  // Delivery point is the one facet whose value is TENANT-SPECIFIC (a point
  // name only means anything inside one tenant), so it persists tenant-scoped —
  // a plain usePersistedState would carry Magalies' point onto the next
  // tenant's board and silently show it empty.
  const [deliveryPointFilter, setDeliveryPointFilter] = useTenantPersistedState<string>(FILTER_STORAGE_KEYS.deliveryPointFilter, '')
  const [fromFilter,    setFromFilter]    = usePersistedState<string>(FILTER_STORAGE_KEYS.fromFilter, '')
  const [toFilter,      setToFilter]      = usePersistedState<string>(FILTER_STORAGE_KEYS.toFilter, '')
  const [searchField,   setSearchField]   = usePersistedState<SearchField>(FILTER_STORAGE_KEYS.searchField, 'any')
  const [searchQuery,   setSearchQuery]   = usePersistedState<string>(FILTER_STORAGE_KEYS.searchQuery, '')

  // The tenant's intake points, for the facet below. Empty for a packhouse that
  // hasn't defined any → the facet never renders (render-on-data-presence, the
  // same rule DeliveryPointPicker follows), so this board is unchanged for them.
  const { data: deliveryPoints = [] } = useDeliveryPoints()

  // Default to the last 90 days (the citrus board's rule, shared via
  // useHistoryWindow); an explicit older "From" filter widens the server window
  // so the client date filter never shows empty for want of unloaded rows.
  // This board has no All-history toggle, so the window is never lifted.
  const serverFromIso = useHistoryWindow(false, fromFilter)

  const deliveriesQ = useQuery<DeliveryRow[]>({
    queryKey: ['bb-deliveries', serverFromIso],
    refetchInterval: POLL.standard,
    // Page the window instead of a single capped pull — drains without truncation.
    queryFn: async () => {
      const { rows, error } = await fetchAllPaged<DeliveryRow>((lo, hi) => {
        let q = supabase
          .from('deliveries')
          .select('delivery_id, grower, variety, puc, block, delivery_point, bins_received, bins_remaining, status, delivery_type, fruit_container_type, vehicle_reg, driver_name, received_at, notes')
          .is('deleted_at', null)
        if (serverFromIso) q = q.gte('received_at', serverFromIso)
        return q
          .order('received_at', { ascending: false })
          .range(lo, hi) as unknown as PromiseLike<{ data: DeliveryRow[] | null; error: { message: string } | null }>
      })
      if (error) throw new Error(error)
      return rows
    },
  })
  const deliveries = deliveriesQ.data ?? []
  const isLoading = deliveriesQ.isLoading

  // Per-delivery weigh totals (mig 1045), scoped to the visible deliveries —
  // mirrors how the citrus board pulls delivery_tipping_summary. Independent
  // query (the embed path is fragile); the id list rides in the key so it
  // refetches with the board.
  const deliveryIds = useMemo(() => deliveries.map(d => d.delivery_id), [deliveries])
  const { data: weighByDelivery = new Map<string, DeliverySummary>(), isSuccess: weighsRead } = useQuery({
    queryKey: ['bb-delivery-weights', deliveryIds],
    enabled: deliveryIds.length > 0,
    refetchInterval: POLL.standard,
    // fallow-ignore-next-line complexity
    queryFn: async () => {
      const { data, error } = await supabase.rpc('delivery_weighing_summary', { p_ids: deliveryIds })
      if (error) throw error
      const m = new Map<string, DeliverySummary>()
      for (const r of (data ?? []) as SummaryRow[]) {
        m.set(r.delivery_id, {
          net:     Number(r.net_kg) || 0,
          crates:  Number(r.crates_weighed) || 0,
          // Reject crates + the size split (mig 20260813100000). A delivery
          // received before the split shipped simply has no breakdown, so the
          // card renders exactly as it always did.
          rejects: Number(r.reject_crates) || 0,
          sizes:   Array.isArray(r.size_breakdown) ? r.size_breakdown : [],
        })
      }
      return m
    },
  })

  // The tenant's configured intake classes, purely to ORDER the card chips
  // (Small before Large, not alphabetically). No classes = no ordering needed.
  const { data: intakeSizes = [] } = useCommodityIntakeSizes(useFlowManifest().commodityName)
  const sizeOrder = useMemo(() => intakeSizes.map(s => s.code), [intakeSizes])
  // ESTIMATED net for a delivery that was never weighed, from the newest crate
  // weight sample of its variety on its date. Shown marked "est." on the card;
  // never stored as a weighing.
  const { data: weightSamples = [] } = useCrateWeightSamples()
  const { data: boardTares = {} } = useBerryCrateTares()
  // Only once the weights read has SUCCEEDED (a weighed delivery must never flash
  // an estimate), and never with a placeholder tare: no configured tare = no estimate.
  const estNetOf = (d: DeliveryRow): number | null => !weighsRead ? null : estimateDeliveryNetKg(
    { crates: d.bins_received, variety: d.variety, onDate: sastYMD(new Date(d.received_at)),
      measuredNetKg: weighByDelivery.get(d.delivery_id)?.net },
    weightSamples, configuredTare(boardTares, d.fruit_container_type))
  const tareMissing = (d: DeliveryRow): boolean => weighsRead
    && configuredTare(boardTares, d.fruit_container_type) == null
    && (weighByDelivery.get(d.delivery_id)?.net ?? 0) <= 0
    && sampleAvgFor(weightSamples, d.variety, sastYMD(new Date(d.received_at))) != null

  // Online only: the delivery and the load close in one server call, so a
  // queued save could not tell the farm. Manual intake stays open offline.
  async function startFarmLoad(l: PendingFarmLoad) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      await alertDialog({ title: 'No signal', message: 'Needs signal to receive a farm load. You can still receive crates by hand.' })
      return
    }
    setFarmLoad(l)
    setJustSaved(null)
    setShowForm(true)
  }

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['bb-deliveries'] })
    qc.invalidateQueries({ queryKey: ['bb-delivery-weights'] })
    qc.invalidateQueries({ queryKey: ['precool-deliveries'] })
    qc.invalidateQueries({ queryKey: ['yard-deliveries'] })
  }

  // The citrus statuses in the chip row never occur on a berry tenant, and the
  // berry rows lack block/DN fields — filterDeliveries handles both shapes.
  const filtered = useMemo(() => filterDeliveries(deliveries as unknown as Delivery[], {
    statusFilter, growerFilter, varietyFilter, blockFilter: '', deliveryPointFilter, fromFilter, toFilter,
    searchField, searchQuery,
  }) as unknown as DeliveryRow[],
  [deliveries, statusFilter, growerFilter, varietyFilter, deliveryPointFilter, fromFilter, toFilter, searchField, searchQuery])

  const growerOptions  = useMemo(() => Array.from(new Set(deliveries.map(d => d.grower.trim()))).sort(),  [deliveries])
  const varietyOptions = useMemo(() => Array.from(new Set(deliveries.map(d => d.variety.trim()))).sort(), [deliveries])
  const anyFilterSet = statusFilter !== 'ALL' || !!(growerFilter || varietyFilter || deliveryPointFilter || fromFilter || toFilter || searchQuery.trim())

  return (
    <div className="min-h-full bg-canvas print:bg-white">
      {/* PW-13: the title keeps its row. It sat at 18px under the Report
          button on a phone (packhouse audit PM-DLV-15); the buttons now take
          their own row there, so Report can carry its word at every width. */}
      <PageHeader title="Deliveries" icon={<Truck size={18} className="text-blue-400" />} printHidden
        actions={<>
          <button aria-label="Print report" onClick={() => navigate('/deliveries/report')} title="Open the printable deliveries report"
            className="btn-ghost btn-sm shrink-0 flex items-center gap-1">
            <Printer size={14} /> Report
          </button>
          <button aria-label="Cultivar report" onClick={() => navigate('/deliveries/cultivar-report')} title="Kg in, waste and packed output per cultivar per week"
            className="btn-ghost btn-sm shrink-0 flex items-center gap-1">
            <FileText size={14} /> Cultivar report
          </button>
          <button aria-label="Crate weights" onClick={() => navigate('/deliveries/crate-weights')} title="Crate weight samples that estimate the kg of unweighed deliveries"
            className="btn-ghost btn-sm shrink-0 flex items-center gap-1">
            <Scale size={14} /> Crate weights
          </button>
          <button onClick={() => { setShowForm(s => !s); setFarmLoad(null); setJustSaved(null) }}
            className="btn-primary flex items-center gap-1.5 text-sm shrink-0">
            <Plus size={16} /> Receive crates
          </button>
        </>} />

      {/* Search row — the shared field-scoped row (citrus board + Receive-pallets register) */}
      <div className="print:hidden">
        <FieldScopedSearch
          fields={BERRY_SEARCH_FIELDS}
          field={searchField}
          onField={setSearchField}
          query={searchQuery}
          onQuery={setSearchQuery}
          noun="deliveries"
        />
      </div>

      {/* Status chips */}
      <div className="flex gap-2 px-4 py-2 overflow-x-auto scrollbar-hide items-center print:hidden">
        {BERRY_STATUS_FILTERS.map(f => (
          <button key={f} onClick={() => setStatusFilter(f)}
            className={statusFilter === f
              ? 'shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold bg-green-600 text-white'
              : 'shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold bg-raised text-gray-400 hover:bg-raised-hover'}>
            {BERRY_STATUS_LABELS[f]}
          </button>
        ))}
        {anyFilterSet && (
          <button
            onClick={() => { setStatusFilter('ALL'); setGrowerFilter(''); setVarietyFilter(''); setDeliveryPointFilter(''); setFromFilter(''); setToFilter(''); setSearchQuery('') }}
            className="shrink-0 ml-auto text-xs text-blue-400 hover:text-blue-300 underline-offset-4 hover:underline">
            Clear filters
          </button>
        )}
      </div>

      {/* Grower / variety / date filters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-4 py-2 border-b border-line-subtle bg-canvas/40 print:hidden">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-gray-500">Grower</span>
          <select value={growerFilter} onChange={e => setGrowerFilter(e.target.value)}
            className="w-full mt-0.5 bg-surface border border-line rounded-lg px-2 py-1 text-xs text-white light:text-fg">
            <option value="">All</option>
            {growerOptions.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-gray-500">Variety</span>
          <select value={varietyFilter} onChange={e => setVarietyFilter(e.target.value)}
            className="w-full mt-0.5 bg-surface border border-line rounded-lg px-2 py-1 text-xs text-white light:text-fg">
            <option value="">All</option>
            {varietyOptions.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        {/* Delivery point — offered to a tenant that has defined points, so this
            row keeps its shipped four columns everywhere else. A still-set
            filter keeps the facet visible even after the last point is
            deactivated, so a stale filter can always be seen and cleared rather
            than silently emptying the board. Filters on
            the NAME snapshot (deliveries.delivery_point) the DB stamps; the
            "Unspecified" sentinel picks the no-point rows, which is every
            delivery received before a point existed. */}
        {(deliveryPoints.length > 0 || !!deliveryPointFilter) && (
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-gray-500">Delivery point</span>
            <select value={deliveryPointFilter} onChange={e => setDeliveryPointFilter(e.target.value)}
              className="w-full mt-0.5 bg-surface border border-line rounded-lg px-2 py-1 text-xs text-white light:text-fg">
              <option value="">All</option>
              {deliveryPoints.map(p => (
                <option key={p.delivery_point_id} value={p.name}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>
              ))}
              <option value={UNSPECIFIED_DELIVERY_POINT}>Unspecified</option>
            </select>
          </label>
        )}
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-gray-500">From</span>
          <input type="date" value={fromFilter} onChange={e => setFromFilter(e.target.value)}
            className="w-full mt-0.5 bg-surface border border-line rounded-lg px-2 py-1 text-xs text-white light:text-fg" />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-gray-500">To</span>
          <input type="date" value={toFilter} onChange={e => setToFilter(e.target.value)}
            className="w-full mt-0.5 bg-surface border border-line rounded-lg px-2 py-1 text-xs text-white light:text-fg" />
        </label>
      </div>
      {!fromFilter && (
        <p className="px-1 -mt-1 text-[10px] text-gray-500">
          Showing the last 90 days · set a <span className="text-gray-400">From</span> date to load older deliveries.
        </p>
      )}

      <div className="p-4 max-w-3xl mx-auto space-y-4 print:max-w-none">
        {justSaved && (
          <SavedBanner
            onDismiss={() => setJustSaved(null)}
            actions={<>
              <SavedBannerLink to={coolTheCratesHref(justSaved)}>Cool the crates →</SavedBannerLink>
              {canOpenPrecool && dollyLabelHref(justSaved) && (
                <SavedBannerLink to={dollyLabelHref(justSaved)!}>Print its label</SavedBannerLink>
              )}
            </>}>
            <span>{justSaved.summary}</span>
          </SavedBanner>
        )}

        {!showForm && (
          <BerryFarmLoadsInbox tenantId={tenantId} onReceive={l => void startFarmLoad(l)} />
        )}

        {showForm && (
          <IntakeForm
            key={farmLoad?.load_id ?? 'manual'}
            farmLoad={farmLoad}
            onClose={() => { setShowForm(false); setFarmLoad(null) }}
            onCreated={(saved) => {
              setShowForm(false); setFarmLoad(null); setJustSaved(saved); refresh()
              void qc.invalidateQueries({ queryKey: ['pending-farm-loads'] })
            }}
          />
        )}

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading deliveries…</p>
        ) : filtered.length === 0 ? (
          /* Gate the "No deliveries yet" claim — a paused/errored fetch must
             render an honest offline/retry state, never a confident empty
             board with a receive-CTA (idle-wake UI-4; audit FC-09). A
             filters-empty with data loaded is a client-side truth, no gate. */
          deliveries.length > 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-fg-muted text-center print:hidden">
              <Truck size={48} className="mb-3 opacity-40" />
              <p className="font-medium">No deliveries match the filters</p>
              <p className="text-sm mt-1 max-w-xs">Adjust or clear the filters above.</p>
            </div>
          ) : (
            <QueryStateGate query={deliveriesQ} noun="deliveries"
              empty={
                <div className="flex flex-col items-center justify-center py-16 text-fg-muted text-center print:hidden">
                  <Truck size={48} className="mb-3 opacity-40" />
                  <p className="font-medium">No deliveries yet</p>
                  <p className="text-sm mt-1 max-w-xs">Receive the first crates with the button above.</p>
                </div>
              } />
          )
        ) : (
          <div className="space-y-2">
            {/* fallow-ignore-next-line complexity */}
            {filtered.map(d => {
              const remaining = d.bins_remaining
              const weigh = weighByDelivery.get(d.delivery_id)
              return (
                <div key={d.delivery_id}
                  className="card p-3 space-y-1.5 print:border print:border-gray-300 print:bg-white print:shadow-none">
                  <div className="flex items-start justify-between gap-2">
                    {/* Row click opens the delivery detail (operator request).
                        Sibling anchor to the Precool badge — no <a> nesting. */}
                    <Link to={`/deliveries/${d.delivery_id}`} className="min-w-0 flex-1 group">
                      <p className="font-semibold text-fg text-sm truncate group-hover:text-blue-300 print:text-black">
                        {d.grower} · {d.variety}
                        <ChevronRight size={12} className="inline ml-1 opacity-50 print:hidden" />
                      </p>
                      <p className="text-xs text-fg-muted print:text-gray-700">
                        {d.bins_received} × {d.fruit_container_type ? CONTAINER_LABEL[d.fruit_container_type] : 'crate'}
                        {weigh && weigh.net > 0 && <> · <span className="text-fg-secondary print:text-black">{weigh.net.toFixed(1)} kg net</span></>}
                        {estNetOf(d) != null && <> · <span className="text-fg-secondary print:text-black">~{Math.round(estNetOf(d) ?? 0)} kg net est.</span></>}
                        {tareMissing(d) && <> · <span className="text-amber-300 print:text-black">est. needs crate tare</span></>}
                        {d.puc && <> · PUC {d.puc}</>}
                        {d.block && <> · Block {d.block}</>}
                        {d.delivery_point && <> · Point {d.delivery_point}</>}
                        {d.vehicle_reg && <> · {d.vehicle_reg}</>}
                        {' · '}{fmt(d.received_at)}
                      </p>
                      {/* The size split, when the load was received sorted
                          (mig 20260813100000). Rejects are chipped apart in
                          amber and labelled "not counted" because they sit
                          OUTSIDE bins_received above — without that the two
                          numbers look like they disagree. */}
                      {weigh && weigh.sizes.length > 0 && (
                        <SizeChips rows={weigh.sizes} order={sizeOrder}
                          rejects={weigh.rejects} cratesReceived={d.bins_received} />
                      )}
                    </Link>
                    <div className="text-right shrink-0">
                      {remaining > 0 ? (
                        <Link to={`/precool?delivery=${d.delivery_id}`}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-blue-700/50 bg-blue-900/40 text-blue-200 hover:bg-blue-900/60 print:hidden">
                          <Snowflake size={12} /> {remaining} to cool <ChevronRight size={12} />
                        </Link>
                      ) : (
                        <span className="text-[10px] text-emerald-400 font-semibold print:hidden">All crates batched</span>
                      )}
                      <span className="hidden print:inline text-xs text-gray-700">{remaining} of {d.bins_received} uncooled</span>
                    </div>
                  </div>
                  {d.notes && <p className="text-[11px] text-fg-muted italic truncate print:text-gray-700">{d.notes}</p>}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Crate intake form
// ---------------------------------------------------------------------------
// Cohesive intake form + save that mirrors the (inherited) citrus Deliveries
// IntakeForm; the optional grower-block picker is the only delta here.
// fallow-ignore-next-line complexity
function IntakeForm({ farmLoad, onClose, onCreated }: { farmLoad: PendingFarmLoad | null; onClose: () => void; onCreated: (saved: BerryReceiveSaved) => void }) {
  const saveDb = useSaveDb()
  const { data: growers = [] } = useGrowers()
  const { data: vehicleRegs = [] } = useVehicleRegs()
  // Registry-driven variety suggestions for the ACTIVE flow's commodity
  // (commodity_varieties) — so a strawberry tenant suggests strawberry
  // cultivars, not blueberry ones; typed entry stays allowed for varieties not
  // yet in the registry.
  // WHICH COMMODITY IS ON THE TRUCK — not which one the tenant's single
  // `production_flow` says. Magalies receives blueberries AND strawberries
  // through one BLUEBERRY-flow tenant, so keying these registry lookups off the
  // flow asked for the wrong fruit's varieties and never asked for the other's
  // intake size classes at all. `enabled_flows` already lists both; nothing was
  // reading it.
  //
  // DERIVED, never seeded from the query. enabledKeys/defaultFlow are react-query
  // data and this form can open before they resolve — seeding useState at mount
  // would pin a stale flow for the life of the dialog, which is exactly the
  // defect #1816 fixed on the vehicle picker. `pickedFlow` is also validated
  // against the allow-list, so a lane withdrawn mid-session cannot persist.
  const { defaultFlow, enabledKeys, isMixed } = useTenantFlows()
  const [pickedFlow, setPickedFlow] = useState('')
  // A farm load names its commodity. DERIVED each render (never seeded from the
  // query), and the receiver's own pick wins once made.
  const { data: allCommodities = [] } = useCommodities()
  const loadFlow = resolveEntityFlowKey(allCommodities, farmLoad?.commodity_id) ?? ''
  const loadCommodityName = allCommodities.find(c => c.commodity_id === farmLoad?.commodity_id)?.name ?? null
  const intakeFlow = intakeFlowKey(pickedFlow || loadFlow, enabledKeys, defaultFlow)
  // Mixed tenant: nothing below the fruit tiles shows until a fruit is picked
  // (or the farm load names one).
  const fruitKey = pickedFlow || loadFlow
  const fruitReady = !isMixed || !!fruitKey
  const manifest = useFlowManifest(intakeFlow)
  const varietiesQ = useCommodityVarieties(manifest.commodityName)
  const varieties = useMemo(() => varietiesQ.data ?? [], [varietiesQ.data])
  const varietyListState = readState(varietiesQ)
  // Pick-from-list needs a usable, non-empty list. Anything else keeps the old
  // free-text box so a failed read or an unset-up fruit never blocks a receipt.
  const varietyPickable = varietyListState === 'usable' && varieties.length > 0
  const fruitWords = flowLabel(intakeFlow).toLowerCase()
  // Per-business switch: ask for the driver (system_config.intake_ask_driver).
  // Same key + row shape as SystemConfigSection, so a Config save refreshes it.
  // FAIL OPEN: only an explicit 'false' hides the box.
  const tenantId = useActiveTenantId()
  const askDriverKeys = ['intake_ask_driver']
  const askDriverQ = useQuery({
    queryKey: ['sys-config', tenantId, askDriverKeys.join(',')],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('system_config')
        .select('config_key, config_value')
        .eq('tenant_id', tenantId)
        .in('config_key', askDriverKeys)
      if (error) throw error
      return data as { config_key: string; config_value: string }[]
    },
  })
  const askDriver = (askDriverQ.data ?? []).find(r => r.config_key === 'intake_ask_driver')?.config_value?.trim().toLowerCase() !== 'false'
  // The tenant's INTAKE grading classes for this flow's commodity (Small /
  // Medium / Large — mig 20260813100000). Empty for a packhouse that hasn't
  // configured any, and the whole split section then renders nothing, so this
  // page is unchanged for anyone who receives loads unsorted.
  const { data: intakeSizes = [] } = useCommodityIntakeSizes(manifest.commodityName)
  const { data: drivers = [] } = useQuery<string[]>({
    queryKey: ['delivery-drivers-history'],
    staleTime: 60_000,
    // fallow-ignore-next-line complexity
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deliveries').select('driver_name')
        .not('driver_name', 'is', null)
        .is('deleted_at', null)
        .order('received_at', { ascending: false })
        .limit(500)
      if (error) throw error
      const set = new Set<string>()
      for (const r of (data ?? []) as { driver_name: string | null }[]) {
        if (r.driver_name) set.add(r.driver_name.trim())
      }
      return Array.from(set).filter(Boolean).sort()
    },
  })

  // Seeded ONCE from the load (the load is in hand at mount, unlike query data).
  const prefill = useMemo(() => (farmLoad ? farmLoadPrefill(farmLoad, null) : null), [farmLoad])
  // A packhouse with one grower should not have to pick it. DERIVED (the list
  // arrives after mount); the receiver's own pick, and a farm load's, win.
  const [growerTyped, setGrowerTyped] = useState<string | null>(prefill ? prefill.growerId : null)
  const growerId = growerTyped ?? (growers.length === 1 ? growers[0].grower_id : '')
  const setGrowerId = (v: string) => setGrowerTyped(v)
  // variety / puc / block are DERIVED while the receiver has not typed (null):
  // the load, then the block it names, resolve whenever their data lands, in any
  // order, so the form never depends on which query came back first.
  const [varietyTyped, setVarietyTyped] = useState<string | null>(farmLoad ? null : '')
  const [pucTyped, setPucTyped]         = useState<string | null>(farmLoad ? null : '')
  const [blockTyped, setBlockTyped]     = useState<string | null>(farmLoad ? null : '')
  const setVariety = (v: string) => setVarietyTyped(v)
  const setPuc     = (v: string) => setPucTyped(v)
  const setBlockId = (v: string) => setBlockTyped(v)
  const [crateTypePicked, setCrateTypePicked] = useState<ContainerType>('BLUEBERRY_CRATE')
  // The fruit decides the crate. Only an unrecognised commodity keeps the select.
  const derivedCrate = crateTypeForCommodity(manifest.commodityName)
  const crateType: ContainerType = derivedCrate ?? crateTypePicked
  const [crates, setCrates]         = useState(prefill?.crates ?? '')
  // Where the load came in (mig 20260727090100). OPTIONAL — null saves exactly
  // as before, and the picker renders nothing at all for a tenant with no
  // points defined. Remembered per TENANT: the next truck usually rolls into
  // the same dock, and a plain key would carry one tenant's point id onto
  // another's intake form.
  const [deliveryPointId, setDeliveryPointId] = useTenantPersistedState<string>('kf49.deliveries.lastDeliveryPoint', '')
  const { data: intakePoints = [] } = useDeliveryPoints()
  // Only ever send a point that is still in the live registry. A remembered id
  // whose point has since been deactivated drops out of the picker (so the
  // operator sees "not recorded") while the stale id lingers in localStorage —
  // sending it would stamp a point nobody can see on screen.
  const activePointId = intakePoints.some(p => p.delivery_point_id === deliveryPointId)
    ? deliveryPointId
    : null
  const [vehicleReg, setVehicleReg] = useState(prefill?.vehicle ?? '')
  const [driverName, setDriverName] = useState('')
  // Transport fold. The form always opens empty, so it starts closed.
  const [transportOpen, setTransportOpen] = useState(() => !!(vehicleReg || driverName))
  // The block's variety when it is not in the fruit's list (amber note).
  const [blockVarietyMiss, setBlockVarietyMiss] = useState<string | null>(null)
  // A variety TYPED before the list was pickable (cold-offline start, slow first
  // load) that is not in the list once it arrives.
  const [typedVarietyMiss, setTypedVarietyMiss] = useState<string | null>(null)
  const [notes, setNotes]           = useState(prefill?.notes ?? '')
  const [error, setError]           = useState<string | null>(null)
  // Optional weight capture (mig 1045). Off by default — many berry deliveries
  // aren't weighed at the gate. When on, the operator captures weight one of two
  // ways (weighMode), both of which record GROSS and let the DB derive net
  // (= gross − crates × tare):
  //   AVERAGE (default) — one figure: avg gross per crate × crate count. For the
  //     common gate case where crates weren't each weighed but a typical crate is.
  //   GROUP — one-or-more { crates, gross } rows, a pallet/dolly weighed at a time.
  // Only the ACTIVE mode is ever submitted; the two never both contribute.
  const [weighing, setWeighing]         = useState(false)
  const [weighMode, setWeighMode]       = useState<'AVERAGE' | 'GROUP'>('AVERAGE')
  const [weighGroups, setWeighGroups]   = useState<{ crateCount: string; grossKg: string }[]>([{ crateCount: '', grossKg: '' }])
  // AVERAGE-mode inputs: avg gross weight of one full crate × how many crates.
  // avgCrates tracks the delivery's crate count until the operator hand-edits it
  // (avgCratesTouched) — so correcting the delivery count after toggling weigh on
  // can't leave the average silently under-covering the load.
  const [avgCrates, setAvgCrates]       = useState(prefill?.crates ?? '')
  const [avgCratesTouched, setAvgCratesTouched] = useState(false)
  const [avgGross,  setAvgGross]        = useState('')
  // Optional size split (mig 20260813100000) — the load arrived already sorted,
  // so many crates of each class plus some rejects. Off by default. Keyed by
  // class CODE rather than held as a positional array, so adding or renaming a
  // class in Config can never shuffle half-typed counts onto the wrong size.
  const [splitBySize, setSplitBySize] = useState(false)
  const [sizeCounts,  setSizeCounts]  = useState<Record<string, { crates: string; grossKg: string }>>({})

  // The grower's registered orchard blocks (across all their PUCs). Scoped to
  // the picked grower; reset whenever the grower changes so a stale block from
  // a previously-selected grower can't be saved against the new one.
  const { data: growerBlocks = [] } = useGrowerBlocks(growerId || null)

  // The load names the FARM's block. This packhouse may hold a mirror of it, so
  // read the mirrors (own tenant only, by RLS) and let pickLoadBlock choose.
  const loadBlockId = farmLoad?.block_id ?? null
  const { data: mirrorIds } = useQuery({
    queryKey: ['farm-load-block-mirrors', loadBlockId],
    enabled: !!loadBlockId,
    queryFn: async () => {
      const { data, error } = await supabase.from('blocks')
        .select('block_id').eq('master_block_id', loadBlockId!).is('deleted_at', null)
      if (error) throw error
      return (data ?? []).map(r => r.block_id as string)
    },
  })
  const loadBlockPick = farmLoad ? pickLoadBlock(growerBlocks, loadBlockId, mirrorIds ?? []) : ''
  const blockId = blockTyped ?? loadBlockPick
  const pickedBlock = growerBlocks.find(x => x.block_id === blockId)
  const loadVarietyText = farmLoad ? farmLoadPrefill(farmLoad, loadCommodityName).variety : ''
  const variety = varietyTyped ?? (loadVarietyText || (blockTyped === null ? pickedBlock?.variety ?? '' : ''))
  const puc = pucTyped ?? (blockTyped === null ? pickedBlock?.puc_code ?? '' : '')
  // Variety first, then only the blocks that grow it (or record no variety).
  const blockChoices = blocksForVariety(growerBlocks, variety)

  // The list can arrive AFTER a variety was typed. Once it is pickable only a
  // list name may stay: map the typed text to its list spelling, else blank it
  // and say what was typed. Without this the select shows "— pick a variety —"
  // while the typed text is what saves — the no-fruit load this pick-list exists
  // to stop. A list pick always matches, so this never fights the operator.
  useEffect(() => {
    if (!varietyPickable) return
    const typed = variety.trim()
    if (!typed || varieties.some(v => v.name === variety)) return
    const hit = varieties.find(v => v.name.trim().toLowerCase() === typed.toLowerCase())
    if (hit) { setVariety(hit.name); return }
    setVariety('')
    setTypedVarietyMiss(typed)
  }, [varietyPickable, varieties, variety])

  // Optional intake weight (mig 1045). Per-crate tare from bin_types — the same
  // source Precool uses — with a placeholder + guard when it isn't configured.
  const { data: crateTares = {} } = useBerryCrateTares()
  const tareKg     = crateTares[crateType] ?? PLACEHOLDER_CRATE_TARE_KG
  const tareUnset  = crateTares[crateType] == null || crateTares[crateType] <= 0

  // The split, folded. One line per configured class + the reject line.
  const sizeLines: SizeLineInput[] = useMemo(
    () => buildSizeLines(intakeSizes.map(s => s.code), sizeCounts, REJECT_LINE_KEY),
    [intakeSizes, sizeCounts])
  const split = sizeSplitTotals(sizeLines, tareKg)

  // With the split ON the delivery's crate count IS the sum of its FRUIT lines.
  // Deriving it rather than asking for it again is the whole point: a separate
  // editable total is double entry, and the two silently disagreeing is how a
  // grower ends up queried on a load nobody can reconcile. Rejects stay out
  // (decision 2026-08-12) so Precool is never offered a crate of waste.
  const crateTotal = splitBySize ? split.fruitCrates : (parseInt(crates, 10) || 0)
  const parsedGroups: WeighGroupInput[] = weighGroups.map(g => ({
    crateCount: parseInt(g.crateCount, 10) || 0,
    grossKg:    parseFloat(g.grossKg)      || 0,
  }))
  // AVERAGE mode collapses to ONE synthetic weigh group so everything downstream
  // (weighTotals, the submit guard, the weighings array) is identical to group
  // mode. Only the active mode's inputs feed groupsToUse.
  const avgGroup = avgModeToGroup(parseFloat(avgGross) || 0, parseInt(avgCrates, 10) || 0)
  const groupsToUse: WeighGroupInput[] = weighMode === 'AVERAGE' ? [avgGroup] : parsedGroups
  const totals = weighTotals(groupsToUse, tareKg)
  // "Receive & put on dolly" needs SOME gross for the batch: the captured
  // intake weight, else the per-variety standard (mig 20260729150622).
  const defaultsCfg = useCoolingDefaults()
  // A QC crate-weight sample for this variety (newest, on today) beats the
  // static config standard; both are estimates, never stored as a weighing.
  const { data: weightSamples = [] } = useCrateWeightSamples()
  const stdKgForVariety = sampleAvgFor(weightSamples, variety, sastTodayYMD()) ?? stdCrateGrossKg(defaultsCfg, variety)
  // Whichever capture is live supplies the dolly's gross: the size lines when
  // the split is on, the weigh groups otherwise, else the variety standard.
  // L51: splitDollyGross returns 0 unless EVERY fruit crate in the split
  // carries a weight, so a part-weighed split falls through to the variety
  // standard below rather than spreading the weighed subset's gross across the
  // full crate count.
  const capturedGross = splitBySize
    ? splitDollyGross(split)
    : (weighing && !totals.hasInvalid ? totals.gross : 0)
  const comboWeightReady = capturedGross > 0 || stdKgForVariety != null
  const primaryAction = primaryReceiveAction(comboWeightReady)
  // AVERAGE-mode live readout: the derived net and implied fruit-per-crate the
  // grower is paid on (a fruit figure that reads absurdly low catches a clerk
  // who typed a fruit-only estimate into this gross field).
  const avgNet = weighGroupNetKg(avgGroup.grossKg, avgGroup.crateCount, tareKg)
  const avgFruitPerCrate = avgNet != null && avgGroup.crateCount > 0 ? avgNet / avgGroup.crateCount : null
  const patchGroup  = (i: number, patch: Partial<{ crateCount: string; grossKg: string }>) =>
    setWeighGroups(gs => gs.map((g, j) => (j === i ? { ...g, ...patch } : g)))
  const addGroup    = () => setWeighGroups(gs => [...gs, { crateCount: '', grossKg: '' }])
  const removeGroup = (i: number) => setWeighGroups(gs => (gs.length > 1 ? gs.filter((_, j) => j !== i) : gs))

  const create = useMutation({
    // Validation-heavy atomic receive, same shape as the inherited citrus save;
    // the block_id + puc-from-block derivation is the only delta here.
    // fallow-ignore-next-line complexity
    mutationFn: async (mode: 'receive' | 'dolly') => {
      // Isolated save client + timeout: the shared client's auth lock can hold
      // a write open forever (the "stuck on Saving…" class useSaveDb exists for).
      const db = saveDb ?? await ensureSaveDb()
      if (!db) throw new ValidationError('Session not ready — refresh and try again.')
      const grower = growers.find(g => g.grower_id === growerId)
      if (!grower) throw new ValidationError('Pick the grower these crates came from')
      if (!variety.trim()) throw new ValidationError('Enter the variety')
      // Optional block — a block belongs to exactly one PUC, so when one is
      // picked we snapshot the full grower→puc→block chain (puc_id/puc derived
      // from the block) for traceability. A hand-typed PUC still wins as the
      // displayed code if the operator entered one.
      const block = blockId ? growerBlocks.find(b => b.block_id === blockId) : undefined
      // H21 — the picker also lists an ACTIVE-linked farm tenant's orchard
      // blocks read live (origin='farm'); those farm rows are deliberately NOT
      // mirrored into the packhouse blocks/pucs tables. deliveries.puc_id /
      // .block_id are FKs that validate row existence regardless of RLS, so
      // persisting a farm-origin id writes a cross-tenant FK reference — and
      // because delete_block/delete_puc count deliveries RLS-free, it would
      // block the farm from deleting its own block/PUC. Only snapshot the ids
      // for a packhouse-owned block; the human-readable block/puc text below is
      // always kept for traceability + display.
      const ownBlock = block?.origin === 'packhouse' ? block : undefined
      // crateTotal is the split's fruit sum when the split is on, the typed
      // count otherwise — one number, whichever way it was captured.
      const n = crateTotal
      if (splitBySize) {
        if (split.hasInvalid) {
          throw new ValidationError(`Check the size rows — each needs a crate count, and any weight must exceed that many crates’ tare (${tareKg.toFixed(2)} kg each).`)
        }
        if (n < 1) throw new ValidationError('Enter how many crates of each size — the delivery’s crate count comes from the split.')
      } else if (!Number.isFinite(n) || n < 1) {
        throw new ValidationError('Crate count must be at least 1')
      }
      const { data: { session } } = await supabase.auth.getSession()
      const userId = session?.user?.id
      if (!userId) throw new ValidationError('Not signed in — refresh and try again.')

      // Optional weight (mig 1045). Empty inputs record no weighing — a count-only
      // receive saves exactly as before. Validate only what the operator started.
      // Skipped entirely when the split is on: the size rows then own the weights
      // and `weighing` is forced off, so there is nothing here to validate.
      if (weighing && !splitBySize) {
        if (weighMode === 'AVERAGE') {
          // "Started" = a per-crate weight was typed (the crate count is pre-filled
          // context). Only then must it make a valid positive net.
          if ((parseFloat(avgGross) || 0) > 0) {
            if (avgGroup.crateCount < 1) throw new ValidationError('Enter the number of crates for the average.')
            if (avgNet == null) throw new ValidationError(`Average gross per crate must be more than the empty crate (${tareKg.toFixed(2)} kg).`)
          }
        } else if (totals.hasInvalid) {
          throw new ValidationError('Check the weigh groups — each needs a crate count and a gross weight, and the gross must exceed the crates’ tare.')
        }
      }
      // groupsToUse is the active mode's groups (AVERAGE = one derived group);
      // empty groups are filtered out so weight stays optional. AVERAGE rows carry
      // a note so an estimated (not individually-weighed) figure is flagged in the
      // audit trail — the RPC persists it (NULLIF(w->>'note','')), the same marking
      // the manager add-form applies. GROUP rows pass note:null → no note, unchanged.
      //
      // With the split ON, the size rows ARE the intake lines — one line per
      // size (plus rejects), each carrying its own optional weight. They go
      // down the same `weighings` payload, which is why there is exactly ONE
      // set of lines per delivery and no way to weigh the same crates twice.
      const weighNote = weighMode === 'AVERAGE' ? `Avg ${parseFloat(avgGross) || 0} kg/crate` : null
      const weighings = splitBySize
        ? sizeLinesToPayload(sizeLines, crateType, tareKg)
        : weighing
          ? groupsToUse
              .filter(g => g.grossKg > 0 && g.crateCount > 0)
              .map(g => ({ crate_type: crateType, crate_count: g.crateCount, gross_weight_kg: g.grossKg, crate_tare_kg: tareKg, note: weighNote, size_class: null, is_reject: false }))
          : []

      const deliveryPayload = {
        grower:               grower.name,
        grower_id:            grower.grower_id,
        variety:              variety.trim().toUpperCase(),
        puc:                  puc.trim() || block?.puc_code || null,
        puc_id:               ownBlock ? ownBlock.puc_id : null,
        block:                block?.block_name ?? null,
        block_id:             ownBlock ? blockId : null,
        // ID ONLY. deliveries_stamp_delivery_point_trg rejects a cross-tenant
        // point and STAMPS the delivery_point name snapshot server-side, so
        // sending a name here would be pointless (it is overwritten) and is the
        // one way the id/name pair could be made to disagree. Both the RPC and
        // the direct insert below carry the same key.
        delivery_point_id:    activePointId,
        bins_received:        n,
        bins_remaining:       n,
        fruit_container_type: crateType,
        // The Export/Local toggle left the form 2026-07-29 (nothing berry-side
        // reads it — audit). Keep stamping the long-standing default so the
        // column stays populated for reports; citrus intake still captures it.
        delivery_type:        'EXPORT',
        status:               'RECEIVED',
        source:               'MANUAL',
        uses_kf49_bins:       false,      // berries arrive in crates — no KF49 bin loop
        vehicle_reg:          vehicleReg.trim() || null,
        driver_name:          askDriver ? (driverName.trim() || null) : null,
        notes:                notes.trim() || null,
        received_by:          userId,
      }

      // With weight: one atomic RPC writes the delivery + its weigh groups in a
      // single transaction (mig 1045) so a weighed load can't half-save. Without
      // weight: the proven direct insert — now returning the new row's id so
      // the combo save below can hang a dolly off it.
      let newDeliveryId: string | null = null
      if (farmLoad) {
        // Online only: delivery + load close in ONE server call (mig 20261140000000).
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          throw new ValidationError('Needs signal to receive a farm load.')
        }
        const { data: rpcId, error } = await withTimeout(
          db.rpc('receive_berry_farm_load', { p_load_id: farmLoad.load_id, p_delivery: deliveryPayload, p_weighings: weighings }),
          'receive farm load',
        )
        if (error) throwFriendly(error, FARM_LOAD_DOMAIN)
        newDeliveryId = (rpcId as string | null) ?? null
      } else if (weighings.length > 0) {
        const { data: rpcId, error } = await withTimeout(
          db.rpc('receive_berry_delivery', { p_delivery: deliveryPayload, p_weighings: weighings }),
          'receive crate delivery',
        )
        if (error) throw error
        newDeliveryId = (rpcId as string | null) ?? null
      } else {
        const { data: row, error } = await withTimeout(
          db.from('deliveries').insert(deliveryPayload).select('delivery_id').single(),
          'receive crate delivery',
        )
        if (error) throw error
        newDeliveryId = (row as { delivery_id: string } | null)?.delivery_id ?? null
      }

      // A count-only split records lines but weighs nothing, so key the net off
      // the captured net rather than "are there lines".
      const capturedNet = splitBySize ? split.fruitNet : totals.net
      const netStr = capturedNet > 0 ? ` · ${capturedNet.toFixed(1)} kg net` : ''
      // Rejects are called out explicitly: they were counted but are NOT in the
      // n above, and an operator who isn't told that will read it as lost fruit.
      const rejStr = splitBySize && split.rejectCrates > 0
        ? ` ${split.rejectCrates} reject crate${split.rejectCrates === 1 ? '' : 's'} recorded separately.`
        : ''
      let dollyStaged = false
      let dollyBatchId: string | null = null
      let summary = `Delivery received — ${n} crate${n === 1 ? '' : 's'} of ${variety.trim().toUpperCase()} from ${grower.name}.${netStr}${rejStr}`

      // "Receive & put on dolly" (Tier 1, 2026-07-29): one save also stages a
      // cooling batch for ALL received crates. Gross comes from what actually
      // exists — the measured intake weight when one was captured, else the
      // per-variety standard crate weight (mig 20260729150622); the button is
      // disabled when neither does. The delivery is already committed by here,
      // so a dolly failure must NOT read as a failed receive — it downgrades
      // to a warning and the operator builds the batch on the board instead.
      if (mode === 'dolly' && newDeliveryId) {
        // Gate the weighed branch on `weighing`, exactly as `comboWeightReady`
        // and the `weighings` array do — otherwise a gross the operator typed
        // then toggled Weighing OFF (e.g. a typo) still lands on the staged
        // cooling batch while the delivery commits count-only, so the batch
        // net disagrees with the delivery. Count-only falls back to the
        // per-variety standard, matching the delivery.
        // capturedGross is already mode-aware (size lines when the split is on,
        // weigh groups otherwise) and already zeroed when the capture is
        // invalid or toggled off, so the batch can never carry a weight the
        // delivery didn't commit. Note it is the FRUIT gross — a dolly of
        // rejects is not a thing, and cooling them would be wrong anyway.
        const dollyGross = capturedGross > 0
          ? +capturedGross.toFixed(1)
          : stdKgForVariety != null ? +(stdKgForVariety * n).toFixed(1) : null
        try {
          if (dollyGross == null || dollyGross <= tareKg * n) throw new Error('no usable weight')
          // Minted here (as Precool does) so the banner can link to this
          // dolly's label. A queued row has no label page until it syncs.
          const batchId = newUuid()
          const status = await insertOrQueueCoolingBatch({
            batch_id:        batchId,
            delivery_id:     newDeliveryId,
            variety:         variety.trim().toUpperCase(),
            crate_type:      crateType,
            crate_count:     n,
            gross_weight_kg: dollyGross,
            crate_tare_kg:   tareKg,
            pulp_in_temp_c:  null,
          })
          dollyStaged = true
          dollyBatchId = status === 'sent' ? batchId : null
          summary += status === 'queued'
            ? ' Dolly saved offline — it appears on the Force-Cooling page after sync.'
            : ' Dolly created — start cooling on the Force-Cooling page.'
        } catch {
          summary += ' ⚠ Delivery saved, but the dolly wasn’t created — build the batch on the Force-Cooling page.'
        }
      }
      return { summary, deliveryId: newDeliveryId, dollyStaged, dollyBatchId }
    },
    onSuccess: (saved) => onCreated(saved),
    onError: (e: Error) => setError(e.message),
  })

  return (
    <div className="card p-4 space-y-3 border border-blue-800/40 print:hidden">
      <p className="text-sm font-semibold text-white light:text-fg">Receive crates</p>
      {farmLoad && (
        <p className="text-caption text-emerald-200 bg-emerald-900/20 rounded px-2 py-1">
          From the farm. Check each figure. Count the crates. Then tap Receive delivery.
          {farmLoad.notes_for_packhouse ? ` Farm note: ${farmLoad.notes_for_packhouse}` : ''}
        </p>
      )}

      <div className="grid grid-cols-2 gap-2">
        <label className="block col-span-2">
          <span className="label">Grower</span>
          <select className="field" value={growerId} disabled={!!farmLoad}
            onChange={e => { setGrowerId(e.target.value); setBlockId('') }}>
            <option value="">— pick a grower —</option>
            {growers.map(g => <option key={g.grower_id} value={g.grower_id}>{g.name}</option>)}
          </select>
        </label>
      </div>

      {isMixed && (
        <div role="group" aria-label="Fruit" className="grid grid-cols-2 gap-2">
          {enabledKeys.map(k => (
            <button key={k} type="button" aria-pressed={fruitKey === k}
              className={`min-h-[44px] rounded-lg border px-3 text-sm font-semibold ${fruitKey === k ? 'border-blue-400 bg-blue-600/30 text-white' : 'border-line-strong text-fg-secondary'}`}
              onClick={() => {
                if (fruitKey === k) return
                setPickedFlow(k)
                // Size classes are per commodity, so counts typed against the
                // previous fruit's codes would be saved under classes that do
                // not belong to this load. Clear them and fold the section shut.
                setSizeCounts({})
                setSplitBySize(false)
                setVariety('')
                setBlockVarietyMiss(null); setTypedVarietyMiss(null)
              }}>
              {flowLabel(k)}
            </button>
          ))}
        </div>
      )}
      {!fruitReady && <p className="text-sm text-fg-secondary">Pick the fruit</p>}

      {fruitReady && (<>
      <div className="grid grid-cols-2 gap-2">
        {varietyPickable ? (
          <div className="col-span-2">
            <Field label="Variety" required>
              <select id="bb-variety" className="field" required value={variety}
                onChange={e => { setVariety(e.target.value); setBlockVarietyMiss(null); setTypedVarietyMiss(null) }}>
                <option value="">— pick a variety —</option>
                {varieties.map(v => <option key={v.variety_id} value={v.name}>{v.name}</option>)}
              </select>
            </Field>
            {blockVarietyMiss && (
              <p className="text-xs text-amber-300 mt-1">
                The block lists variety “{blockVarietyMiss}”, which is not in the {fruitWords} list. Pick the variety, or ask BigAgri support to add “{blockVarietyMiss}”.
              </p>
            )}
            {typedVarietyMiss && (
              <p className="text-xs text-amber-300 mt-1">
                “{typedVarietyMiss}” is not in the {fruitWords} list. Pick the variety, or ask BigAgri support to add “{typedVarietyMiss}”.
              </p>
            )}
          </div>
        ) : (
          <div className="col-span-2">
            <ComboField id="bb-variety" label="Variety" value={variety} onChange={setVariety}
              options={varieties.map(v => v.name)} placeholder="e.g. VENTURA" required />
            {varietyListState === 'unread' && (
              <p className="text-xs text-amber-300 mt-1">Variety list not loaded — type the variety.</p>
            )}
            {varietyListState === 'usable' && varieties.length === 0 && (
              <p className="text-xs text-amber-300 mt-1">
                No varieties are set up for {fruitWords} yet — type it, and ask BigAgri support to add it.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="label">PUC (optional)</span>
          <input className="field font-mono" value={puc} onChange={e => setPuc(e.target.value)} />
        </label>
        <label className="block">
          <span className="label">Block (optional)</span>
          <select className="field" value={blockId}
            disabled={!growerId || growerBlocks.length === 0}
            /* fallow-ignore-next-line complexity */
            onChange={e => {
              const id = e.target.value
              setBlockId(id)
              setBlockVarietyMiss(null); setTypedVarietyMiss(null)
              // Surface the block's PUC code so the operator doesn't retype it —
              // but never clobber a code they've already entered by hand.
              const b = growerBlocks.find(x => x.block_id === id)
              if (b?.puc_code && (pucTyped === null || !pucTyped.trim())) setPuc(b.puc_code)
              // Auto-fill the variety from the block — the block determines its
              // variety (already shown in the option label). Matches the citrus
              // intake (Deliveries.tsx); still editable in the field above.
              // With a pick-list, only a list name may land in the box: match
              // case-insensitively, else blank it and say why.
              if (b?.variety && !variety.trim()) {
                if (!varietyPickable) setVariety(b.variety)
                else {
                  const want = b.variety.trim().toLowerCase()
                  const hit = varieties.find(v => v.name.trim().toLowerCase() === want)
                  setVariety(hit ? hit.name : '')
                  setBlockVarietyMiss(hit ? null : b.variety.trim())
                }
              }
            }}>
            <option value="">
              {!growerId ? '— pick a grower first —'
                : growerBlocks.length === 0 ? '— no blocks on file —'
                : '— pick a block —'}
            </option>
            {blockChoices.blocks.map(b => (
              <option key={b.block_id} value={b.block_id}>
                {b.block_name}{b.variety ? ` (${b.variety})` : ''}{b.origin === 'farm' ? ' · farm' : ''}
              </option>
            ))}
          </select>
          {blockChoices.noMatch && (
            <p className="text-xs text-amber-300 mt-1">No block lists this variety. Showing all blocks.</p>
          )}
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {derivedCrate ? (
          <p className="text-sm text-fg-secondary self-end min-h-[44px] flex items-center">Crate type: {CONTAINER_LABEL[derivedCrate]}</p>
        ) : (
          <label className="block">
            <span className="label">Crate type</span>
            <select className="field" value={crateType} onChange={e => setCrateTypePicked(e.target.value as ContainerType)}>
              {CRATE_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
        )}
        <label className="block">
          <span className="label">Crates</span>
          {splitBySize ? (
            /* DERIVED while the split is on — the size rows below are the count.
               A second editable total would be double entry, and the two are
               free to disagree the moment someone corrects one of them. */
            <div className="field flex items-center justify-between">
              <span className="tabular-nums text-gray-200 light:text-fg">{split.fruitCrates || '—'}</span>
              <span className="text-[10px] text-gray-500">from the split below</span>
            </div>
          ) : (
            <input type="number" inputMode="numeric" min={1} className="field" value={crates}
              onChange={e => { setCrates(e.target.value); if (!avgCratesTouched) setAvgCrates(e.target.value) }} />
          )}
        </label>
        {/* Where the crates came in. Renders NOTHING until the tenant defines
            points, so this grid keeps its four shipped fields for everyone else.
            Deliberately NOT filtered by commodity: one point takes blueberries
            and strawberries on the same day (spec §1). */}
        <DeliveryPointPicker value={activePointId} onChange={id => setDeliveryPointId(id ?? '')} />
      </div>

      {/* Split by size (mig 20260813100000). Renders ONLY for a tenant that has
          configured intake classes for this commodity — no classes, no section,
          so a packhouse receiving unsorted loads sees the page it always saw.
          When ON it REPLACES the weigh capture below: the size rows carry the
          weights, so a delivery has exactly one set of intake lines and the same
          crates can never be weighed twice into one net. */}
      {intakeSizes.length > 0 && (
        <div>
          <div className="flex items-center justify-between">
            <span className="label mb-0 flex items-center gap-1.5">
              <Layers size={13} className="text-blue-400" /> Split by size <span className="text-gray-500 font-normal">(optional)</span>
            </span>
            <button type="button"
              onClick={() => {
                const next = !splitBySize
                // Hand weight capture to the size rows. Leaving the standalone
                // weigh on would write a SECOND set of lines for the same
                // crates and double the delivery's net.
                if (next) setWeighing(false)
                setSplitBySize(next)
              }}
              className={splitBySize
                ? 'text-xs px-3 py-1.5 rounded-lg border border-blue-500 bg-blue-900/30 text-blue-200 font-semibold'
                : 'text-xs px-3 py-1.5 rounded-lg border border-line text-gray-400 hover:border-line-strong'}>
              {splitBySize ? 'On' : 'Off'}
            </button>
          </div>

          {splitBySize && (
            <div className="mt-2 space-y-2 rounded-lg border border-blue-800/40 bg-blue-950/20 p-3">
              <p className="text-[11px] text-gray-400">
                How many crates of each size came off the truck. Weight is optional — leave the kg
                blank to record counts only{tareKg > 0 && <>; net = gross − crates × {tareKg.toFixed(2)} kg tare</>}.
              </p>

              <div className="grid grid-cols-[minmax(0,1fr)_4.5rem_5.5rem_3.5rem] gap-2 items-center">
                <span className="label text-[10px] mb-0">Size</span>
                <span className="label text-[10px] mb-0">Crates</span>
                <span className="label text-[10px] mb-0">Gross kg</span>
                <span className="label text-[10px] mb-0 text-right">Net</span>
                {[
                  ...intakeSizes.map(s => ({ key: s.code, label: s.code, reject: false })),
                  { key: REJECT_LINE_KEY, label: 'Rejects', reject: true },
                ].map(row => (
                  <SizeSplitRow key={row.key} label={row.label} isReject={row.reject}
                    value={sizeCounts[row.key] ?? { crates: '', grossKg: '' }}
                    tareKg={tareKg}
                    onChange={patch => setSizeCounts(s => ({
                      ...s, [row.key]: { ...(s[row.key] ?? { crates: '', grossKg: '' }), ...patch },
                    }))} />
                ))}
              </div>

              {tareKg <= 0 ? (
                <p className="text-[11px] text-amber-300">
                  ⚠ No crate tare set for {CONTAINER_LABEL[crateType]} — net won't deduct crate weight. Set it in Config → Storage → Bin types (admins).
                </p>
              ) : (tareUnset || tareKg === PLACEHOLDER_CRATE_TARE_KG) ? (
                <p className="text-[11px] text-amber-300">
                  ⚠ No confirmed crate tare — deducting the {PLACEHOLDER_CRATE_TARE_KG.toFixed(1)} kg placeholder. Confirm the real empty {CONTAINER_LABEL[crateType]} weight in Config → Storage → Bin types (admins).
                </p>
              ) : null}

              {split.hasInvalid && (
                <p className="text-[11px] text-amber-300">
                  Check the size rows — a weight needs a crate count next to it, and the gross must be
                  more than those crates' tare ({tareKg.toFixed(2)} kg each).
                </p>
              )}

              <div className="border-t border-line-subtle pt-2 flex items-center justify-between text-[11px]">
                <span className="text-gray-400">
                  {split.fruitCrates} crate{split.fruitCrates === 1 ? '' : 's'} received
                  {split.rejectCrates > 0 && (
                    <span className="text-amber-300"> · {split.rejectCrates} reject (not counted)</span>
                  )}
                </span>
                {split.fruitNet > 0 && (
                  <span className="text-fg font-semibold tabular-nums">{split.fruitNet.toFixed(1)} kg net</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Optional weight (mig 1045). "Off" keeps the count-only receive. "On"
          reveals weigh groups: one over all the crates = an average for the
          load; several = weighed a pallet at a time. Net = gross − crates × tare.
          Hidden entirely while the split is on — the size rows above own the
          weights then, and offering both is how a delivery gets weighed twice. */}
      {!splitBySize && (
      <div>
        <div className="flex items-center justify-between">
          <span className="label mb-0 flex items-center gap-1.5"><Scale size={13} className="text-blue-400" /> Weigh crates <span className="text-gray-500 font-normal">(optional)</span></span>
          <button type="button"
            onClick={() => {
              const next = !weighing
              if (next) {
                // Pre-fill the crate count for whichever mode is showing so the
                // operator only types the weight. Seed-once (don't clobber a
                // value they already typed).
                setAvgCrates(a => a || crates)
                setWeighGroups(gs => (gs.length === 1 && !gs[0].crateCount && !gs[0].grossKg ? [{ crateCount: crates, grossKg: '' }] : gs))
              }
              setWeighing(next)
            }}
            className={weighing
              ? 'text-xs px-3 py-1.5 rounded-lg border border-blue-500 bg-blue-900/30 text-blue-200 font-semibold'
              : 'text-xs px-3 py-1.5 rounded-lg border border-line text-gray-400 hover:border-line-strong'}>
            {weighing ? 'On' : 'Off'}
          </button>
        </div>

        {weighing && (
          <div className="mt-2 space-y-2 rounded-lg border border-blue-800/40 bg-blue-950/20 p-3">
            {/* How the weight was captured. AVERAGE (default) = one avg-gross-per-
                crate figure; GROUP = weigh a pallet at a time. Both record gross;
                only the active mode is submitted. Same segmented style as Destination. */}
            <div className="flex gap-2">
              <button type="button"
                onClick={() => setWeighMode('GROUP')}
                className={weighMode === 'GROUP'
                  ? 'flex-1 text-xs px-3 py-1.5 rounded-lg border border-blue-500 bg-blue-900/30 text-blue-200 font-semibold'
                  : 'flex-1 text-xs px-3 py-1.5 rounded-lg border border-line text-gray-400 hover:border-line-strong'}>
                Total gross
              </button>
              <button type="button"
                onClick={() => { setWeighMode('AVERAGE'); setAvgCrates(a => a || crates) }}
                className={weighMode === 'AVERAGE'
                  ? 'flex-1 text-xs px-3 py-1.5 rounded-lg border border-blue-500 bg-blue-900/30 text-blue-200 font-semibold'
                  : 'flex-1 text-xs px-3 py-1.5 rounded-lg border border-line text-gray-400 hover:border-line-strong'}>
                Avg per crate
              </button>
            </div>

            {tareKg <= 0 ? (
              <p className="text-[11px] text-amber-300">
                ⚠ No crate tare set for {CONTAINER_LABEL[crateType]} — net won't deduct crate weight. Set it in Config → Storage → Bin types (admins).
              </p>
            ) : (tareUnset || tareKg === PLACEHOLDER_CRATE_TARE_KG) ? (
              <p className="text-[11px] text-amber-300">
                ⚠ No confirmed crate tare — deducting the {PLACEHOLDER_CRATE_TARE_KG.toFixed(1)} kg placeholder. Confirm the real empty {CONTAINER_LABEL[crateType]} weight in Config → Storage → Bin types (admins).
              </p>
            ) : (
              <p className="text-[11px] text-gray-400">Tare {tareKg.toFixed(2)} kg/crate · net = gross − crates × tare.</p>
            )}

            {/* GROUP mode — the shipped weigh-group rows, unchanged. */}
            {/* fallow-ignore-next-line complexity */}
            {weighMode === 'GROUP' && (<>
            {/* fallow-ignore-next-line complexity */}
            {weighGroups.map((g, i) => {
              const net = weighGroupNetKg(parseFloat(g.grossKg) || 0, parseInt(g.crateCount, 10) || 0, tareKg)
              return (
                <div key={i} className="flex items-end gap-2">
                  <label className="block flex-1">
                    {i === 0 && <span className="label text-[10px]">Crates</span>}
                    <input type="number" inputMode="numeric" min={1} className="field" value={g.crateCount}
                      onChange={e => patchGroup(i, { crateCount: e.target.value })} placeholder="crates" />
                  </label>
                  <label className="block flex-1">
                    {i === 0 && <span className="label text-[10px]">Gross (kg)</span>}
                    <input type="number" inputMode="decimal" min={0} step="0.1" className="field" value={g.grossKg}
                      onChange={e => patchGroup(i, { grossKg: e.target.value })} placeholder="gross" />
                  </label>
                  <div className="w-16 text-right shrink-0 pb-2">
                    <span className="text-xs text-gray-200 light:text-fg tabular-nums">{net != null ? net.toFixed(1) : '—'}</span>
                    <span className="block text-[9px] text-gray-500">kg net</span>
                  </div>
                  {weighGroups.length > 1 && (
                    <button type="button" onClick={() => removeGroup(i)} aria-label="Remove weigh group"
                      className="pb-2 text-gray-500 hover:text-red-400 shrink-0"><Trash2 size={14} /></button>
                  )}
                </div>
              )
            })}

            <div className="flex items-center justify-between pt-1">
              <button type="button" onClick={addGroup} className="text-xs text-blue-300 hover:text-blue-200 flex items-center gap-1">
                <Plus size={12} /> Add weigh group
              </button>
              {totals.groupsWithInput > 0 && !totals.hasInvalid && (
                <div className="text-right text-[11px]">
                  <span className="text-fg font-semibold tabular-nums">{totals.net.toFixed(1)} kg net</span>
                  {totals.avgNetPerCrate != null && <span className="text-gray-500"> · {totals.avgNetPerCrate.toFixed(2)} kg/crate</span>}
                </div>
              )}
            </div>

            {totals.groupsWithInput > 0 && crateTotal > 0 && totals.crates !== crateTotal && (
              <p className="text-[11px] text-amber-300">
                Weighed {totals.crates} of {crateTotal} crate{crateTotal === 1 ? '' : 's'}
                {totals.crates < crateTotal ? ' — some crates not yet weighed.' : ' — more than the delivery count.'}
              </p>
            )}
            <p className="text-[10px] text-gray-500">
              Weigh a pallet at a time — add a group per weighing.
            </p>
            </>)}

            {/* AVERAGE mode — one figure: avg gross of a full crate × crate count.
                Records the SAME single gross weigh group; net is derived from it. */}
            {/* fallow-ignore-next-line complexity */}
            {weighMode === 'AVERAGE' && (
              <div className="space-y-2">
                <div className="flex items-end gap-2">
                  <label className="block flex-1">
                    <span className="label text-[10px]">Crates</span>
                    <input type="number" inputMode="numeric" min={1} className="field" value={avgCrates}
                      onChange={e => { setAvgCrates(e.target.value); setAvgCratesTouched(true) }} placeholder="crates" />
                  </label>
                  <label className="block flex-1">
                    <span className="label text-[10px]">Avg gross / crate (kg)</span>
                    <input type="number" inputMode="decimal" min={0} step="0.1" className="field" value={avgGross}
                      onChange={e => setAvgGross(e.target.value)} placeholder="per crate" />
                  </label>
                </div>
                <p className="text-[10px] text-gray-400">
                  Type the weight of one typical full crate — the berries plus the crate itself. We take the empty-crate weight off; the grower is paid on the net shown below.
                </p>
                <div className="pt-1 text-right">
                  <span className="text-fg font-semibold tabular-nums">
                    {avgNet != null ? `= ${avgNet.toFixed(1)} kg net` : '= — kg net'}
                  </span>
                  {avgNet != null && (
                    <>
                      <span className="block text-[11px] text-gray-400 tabular-nums">
                        {avgGroup.grossKg.toFixed(1)} kg gross − {(avgGroup.crateCount * tareKg).toFixed(1)} kg crate tare
                      </span>
                      {avgFruitPerCrate != null && (
                        <span className="block text-[10px] text-gray-500 tabular-nums">
                          {avgGroup.crateCount} crates · {avgFruitPerCrate.toFixed(2)} kg fruit per crate
                        </span>
                      )}
                    </>
                  )}
                </div>
                {avgGroup.grossKg > 0 && avgGroup.crateCount > 0 && avgNet == null && (
                  <p className="text-[11px] text-amber-300">
                    Avg per crate must be more than the empty crate ({tareKg.toFixed(2)} kg).
                  </p>
                )}
                {avgFruitPerCrate != null && avgFruitPerCrate > PLAUSIBLE_MAX_FRUIT_PER_CRATE_KG && (
                  <p className="text-[11px] text-amber-300">
                    ≈{avgFruitPerCrate.toFixed(0)} kg of fruit per crate looks high — did you enter the whole load instead of one crate?
                  </p>
                )}
                {avgGroup.crateCount > 0 && crateTotal > 0 && avgGroup.crateCount !== crateTotal && (
                  <p className="text-[11px] text-amber-300">
                    Averaging {avgGroup.crateCount} of {crateTotal} crate{crateTotal === 1 ? '' : 's'}
                    {avgGroup.crateCount < crateTotal ? ' — the rest aren’t covered.' : ' — more than the delivery count.'}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      <details open={transportOpen} onToggle={e => setTransportOpen(e.currentTarget.open)}>
        <summary className="cursor-pointer select-none text-sm text-fg-secondary min-h-[44px] flex items-center">
          Transport (optional)
          {[vehicleReg.trim(), askDriver ? driverName.trim() : ''].filter(Boolean).map(v => ` · ${v}`).join('')}
        </summary>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <ComboField id="bb-vehicle" label="Vehicle reg" value={vehicleReg}
            onChange={(v) => setVehicleReg(v.toUpperCase())} options={vehicleRegs} placeholder="e.g. ND 123-456" />
          {askDriver && (
            <ComboField id="bb-driver" label="Driver" value={driverName}
              onChange={setDriverName} options={drivers} placeholder="Driver name" />
          )}
        </div>
      </details>

      <label className="block">
        <span className="label">Notes</span>
        <textarea className="field" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
      </label>

      </>)}

      {error && <p className="text-xs text-red-400">{error}</p>}
      <div className="flex flex-wrap gap-2">
        {/* UF-12: ONE filled primary per footer — the dolly save when it can
            run (it needs a weight), otherwise the plain receive. */}
        <button onClick={() => create.mutate('receive')} disabled={create.isPending}
          className={`${primaryAction === 'receive' ? 'btn-primary' : 'btn-secondary'} disabled:opacity-50`}>
          {create.isPending ? 'Saving…' : 'Receive delivery'}
        </button>
        {/* One save for the truck-to-dolly case: receive + stage a cooling
            batch for all crates. Needs a weight to hang on the batch — the
            captured intake weight or the variety's standard (config). */}
        <button onClick={() => create.mutate('dolly')} disabled={create.isPending || !comboWeightReady}
          title={comboWeightReady ? undefined : 'Weigh the load, or set a standard crate weight for this variety in Config → Storage → Cooling defaults'}
          className={`${primaryAction === 'dolly' ? 'btn-primary' : 'btn-secondary'} disabled:opacity-50`}>
          {create.isPending ? 'Saving…' : 'Receive & put on dolly'}
        </button>
        <button onClick={onClose} className="text-sm text-gray-400 px-3">Cancel</button>
      </div>
      {!comboWeightReady && (
        <p className="text-[11px] text-gray-500">
          “Receive &amp; put on dolly” needs a weight — weigh the load above, or set a standard
          crate weight for this variety in <ConfigLink tab="storage" section="Cooling defaults" newTab>Config → Storage → Cooling defaults</ConfigLink>.
        </p>
      )}
      <p className="text-[11px] text-gray-500">
        Next step: build a cooling batch from these crates in <Link to="/precool" className="text-blue-300 underline">Force-Cooling</Link>.
      </p>
    </div>
  )
}
